import React, { useState, useEffect, useRef } from 'react';
import ArchitectureBlock from './ArchitectureBlock';
import OldActivityArchitecture from './OldActivityArchitecture';
import { BMC_BLOCKS } from '../common_components/bmc/bmc_constants';
import { LEAN_BLOCKS } from './constants';
import { Layer, Stage, Group, Line, Text, Arrow, Rect } from 'react-konva';
import { injectIntl } from 'react-intl';
import { inject, observer } from 'mobx-react';
import messages from '../../locales/igtagMessagesmbc.json';

// Función para obtener las dimensiones de un texto (ancho y alto)
// Se utiliza un canvas para medir el texto dado un estilo de fuente y padding
const getTextDimensions = (text, font = '12px sans-serif', padding = 20) => {
  const canvas = document.createElement('canvas'); // Crea un canvas temporal
  const context = canvas.getContext('2d');
  context.font = font;
  const width = context.measureText(text).width + padding;
  const height = parseInt(font, 10) + padding;
  return { width, height };
};

// Calcula la posición para un "tooltip" basado en la referencia de un botón y las dimensiones del tooltip
const computeTooltipPosition = (
  buttonRef,
  tagDimensions,
  verticalOffset,
  xOffset = 0
) => {
  if (!buttonRef.current) return { x: 0, y: 0 };
  const rect = buttonRef.current.getBoundingClientRect();
  return {
    x: rect.left + (rect.width - tagDimensions.width) / 2 + xOffset,
    y: rect.top - tagDimensions.height - verticalOffset
  };
};

// Componente funcional que maneja la vista de la arquitectura de actividad,
// permite alternar entre dos vistas y renderiza el componente correspondiente.
function ActivityArchitecture({ history }) {
  // Estado para guardar la vista seleccionada, inicializado desde localStorage si existe
  const [selectedView, setSelectedView] = useState(() => {
    const saved = localStorage.getItem('selectedView');
    return saved ? Number(saved) : 1;
  });

  // Estado y referencia para manejar el botón sobre el que se pasa el mouse (hover)
  const [hoveredButton, setHoveredButton] = useState(null);
  const hoverTimer = useRef(null);

  const width = 682;
  const height = 970;

  // Función para definir el estilo de los botones, en función de si están activos o en hover
  const buttonStyle = (active, id) => ({
    margin: '0 10px',
    backgroundColor: active ? '#0fa69a' : '#ddd',
    color: active ? 'white' : '#505050',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '13px',
    borderRadius: '10px',
    padding: '6px 10px',
    opacity: active ? 1 : hoveredButton === id ? 1 : 0.4,
    transition: 'opacity 0.5s ease, background-color 0.3s ease, color 0.3s ease'
  });

  // Selección del idioma basado en el navegador y obtención de textos para los botones
  const locale = navigator.language.split('-')[0];
  const buttonTexts = {
    button_1:
      (messages[locale] && messages[locale].button_1) || messages.es.button_1,
    button_2:
      (messages[locale] && messages[locale].button_2) || messages.es.button_2
  };

  // Guardar la vista seleccionada en localStorage cada vez que cambia el estado

  useEffect(() => {
    localStorage.setItem('selectedView', selectedView);
  }, [selectedView]);

  return (
    <div style={{ position: 'relative' }}>
      {/* Contenedor de botones para alternar vistas */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '10px',
          paddingRight: '20px'
        }}
      >
        <button
          style={buttonStyle(selectedView === 1, 1)}
          onClick={() => setSelectedView(1)}
          onMouseEnter={() => {
            hoverTimer.current = setTimeout(() => setHoveredButton(1), 300);
          }}
          onMouseLeave={() => {
            clearTimeout(hoverTimer.current);
            setHoveredButton(null);
          }}
        >
          {buttonTexts.button_1}
        </button>
        <button
          style={buttonStyle(selectedView === 2, 2)}
          onClick={() => setSelectedView(2)}
          onMouseEnter={() => {
            hoverTimer.current = setTimeout(() => setHoveredButton(2), 300);
          }}
          onMouseLeave={() => {
            clearTimeout(hoverTimer.current);
            setHoveredButton(null);
          }}
        >
          {buttonTexts.button_2}
        </button>
      </div>
      <div className="architecture-block-container">
        {selectedView === 1 ? (
          // Vista principal usando react-konva para renderizar graficos
          <Stage width={width} height={height}>
            <Layer>
              <KeyActivityGraphic width={710} history={history} />
            </Layer>
          </Stage>
        ) : (
          // Vista secundaria que utiliza una version antigua de la arquitectura
          <OldActivityArchitecture history={history} />
        )}
      </div>
    </div>
  );
}

// Componente de clase que representa el gráfico de las actividades clave
@injectIntl
@inject('rootStore')
@observer
class KeyActivityGraphic extends React.Component {
  constructor(props) {
    super(props);
    // Estado inicial para almacenar la altura de la descripción
    this.state = { cardDescriptionHeight: 0 };
    this.handleClick = this.handleClick.bind(this);
  }

  // Maneja el clic en la actividad y redirige a la ruta correspondiente
  handleClick() {
    this.props.history.push(
      this.props.rootStore.blockPath(BMC_BLOCKS.KEY_ACTIVITIES)
    );
  }

  // Retorna un color para una flecha en función de un valor dado
  getArrowColor(val) {
    return val > 0 ? '#7BA3D3' : '#ddd';
  }

  //calcula y retorna posiciones para bloques, lineas y flechas del grafico
  getPositions() {
    //margenes y valores de prueba para definir las posiciones
    const marginy = 65,
      marginx = 43;
    const prueba = 150,
      prueba2 = 314,
      prueba3 = 120,
      prueba4 = 54;
    //posiciones para cada bloque del canvas
    const revenues = { x: marginx, y: marginy };
    const measure = { x: revenues.x + prueba, y: revenues.y };
    const activity = { x: measure.x + prueba, y: revenues.y };
    const proposition = { x: activity.x + prueba, y: revenues.y };
    const resources = { x: marginx, y: revenues.y + prueba2 };
    const customers = { x: resources.x + prueba, y: resources.y };
    const channels = { x: customers.x + prueba, y: resources.y };
    const relations = { x: channels.x + prueba, y: resources.y };
    const partners = { x: marginx, y: relations.y + prueba2 };
    const costs = { x: partners.x + prueba, y: partners.y };
    const learn = { x: costs.x + prueba, y: partners.y };
    const build = { x: learn.x + prueba, y: partners.y };

    const blocks = {
      revenue: revenues,
      measure: measure,
      activity: activity,
      proposition: proposition,
      resources: resources,
      customers: customers,
      channels: channels,
      relations: relations,
      partners: partners,
      costs: costs,
      learn: learn,
      build: build
    };

    // Posiciones para las lineas que conectan los bloques
    const lines = {
      line1: { x: revenues.x + prueba3, y: revenues.y + prueba4 },
      line2: { x: measure.x + prueba3, y: measure.y + prueba4 },
      line3: { x: activity.x + prueba3, y: activity.y + prueba4 },
      line4: { x: resources.x + prueba3, y: resources.y + prueba4 },
      line5: { x: customers.x + prueba3, y: customers.y + prueba4 },
      line6: { x: channels.x + prueba3, y: channels.y + prueba4 },
      line7: { x: partners.x + prueba3, y: partners.y + prueba4 },
      line8: { x: costs.x + prueba3, y: costs.y + prueba4 },
      line9: { x: learn.x + prueba3, y: learn.y + prueba4 }
    };

    // Posiciones y coordenadas para las flechas graficas
    const arrows = {
      arrow1: {
        x1: activity.x + prueba3 + 120,
        y1: activity.y + prueba4,
        x2: activity.x + prueba3 + 180,
        y2: activity.y + prueba4,
        x3: activity.x + prueba3 + 180,
        y3: activity.y + prueba4 + 220
      },
      arrow2: {
        x1: resources.x + prueba3 - 30,
        y1: resources.y + prueba4,
        x2: resources.x + prueba3 - 121,
        y2: resources.y + prueba4,
        x3: resources.x + prueba3 - 121,
        y3: resources.y + prueba4 + 220
      }
    };

    // Dimensiones utilizadas para bloques y líneas
    const dimensions = {
      smallWidth: 150,
      smallHeight: 110,
      lineStrokeWidth: 3
    };

    return { blocks, lines, arrows, dimensions };
  }

  //renderiza las lineas conectando bloques
  renderLines(positions, strokeColor) {
    const { lineStrokeWidth } = positions.dimensions;
    return Object.keys(positions.lines).map((key, index) => (
      <Line
        key={`line-${index}`}
        x={positions.lines[key].x}
        y={positions.lines[key].y}
        points={[0, 0, 60, 0]}
        stroke={strokeColor}
        strokeWidth={lineStrokeWidth}
        lineCap="round"
        lineJoin="round"
      />
    ));
  }

  //renderiza las flechas en el grafico
  renderArrows(positions) {
    const arrowDefaults = {
      stroke: '#d0d0d0',
      strokeWidth: 10,
      pointerLength: 13,
      pointerWidth: 17,
      lineCap: 'butt',
      lineJoin: 'miter'
    };

    return (
      <>
        <Arrow
          points={[
            positions.arrows.arrow1.x1,
            positions.arrows.arrow1.y1,
            positions.arrows.arrow1.x2,
            positions.arrows.arrow1.y2,
            positions.arrows.arrow1.x3,
            positions.arrows.arrow1.y3
          ]}
          {...arrowDefaults}
        />
        <Arrow
          points={[
            positions.arrows.arrow2.x1,
            positions.arrows.arrow2.y1,
            positions.arrows.arrow2.x2,
            positions.arrows.arrow2.y2,
            positions.arrows.arrow2.x3,
            positions.arrows.arrow2.y3
          ]}
          {...arrowDefaults}
        />
      </>
    );
  }

  // Renderiza los bloques que representan las distintas áreas del modelo
  renderBlocks(positions) {
    const { smallWidth, smallHeight } = positions.dimensions;
    const { rootStore, history } = this.props;
    const disableLearnBuild = rootStore.disableLearnBuildBlocks;
    const blockConfigs = [
      {
        block: BMC_BLOCKS.REVENUE_STREAMS,
        pos: positions.blocks.revenue,
        step: 1
      },
      {
        block: LEAN_BLOCKS.MEASURE,
        pos: positions.blocks.measure,
        step: 2
      },
      {
        block: BMC_BLOCKS.KEY_ACTIVITIES,
        pos: positions.blocks.activity,
        step: 3
      },
      {
        block: BMC_BLOCKS.KEY_PROPOSITIONS,
        pos: positions.blocks.proposition,
        step: 4
      },
      {
        block: BMC_BLOCKS.CUSTOMER_RELATIONSHIPS,
        pos: positions.blocks.relations,
        step: 5
      },
      { block: BMC_BLOCKS.CHANNELS, pos: positions.blocks.channels, step: 6 },
      {
        block: BMC_BLOCKS.CUSTOMER_SEGMENTS,
        pos: positions.blocks.customers,
        step: 7
      },
      {
        block: BMC_BLOCKS.KEY_RESOURCES,
        pos: positions.blocks.resources,
        step: 8
      },
      {
        block: BMC_BLOCKS.KEY_PARTNERS,
        pos: positions.blocks.partners,
        step: 9
      },
      {
        block: BMC_BLOCKS.COST_STRUCTURE,
        pos: positions.blocks.costs,
        step: 10
      },
      {
        block: LEAN_BLOCKS.LEARN,
        pos: positions.blocks.learn,
        step: 11,
        disabled: true
      },
      {
        block: LEAN_BLOCKS.BUILD,
        pos: positions.blocks.build,
        step: 12,
        disabled: true
      }
    ];

    return blockConfigs.map(config => (
      <ArchitectureBlock
        key={config.step}
        history={history}
        block={config.block}
        x={config.pos.x}
        y={config.pos.y}
        width={smallWidth}
        height={smallHeight}
        stepNumber={config.step}
        {...(config.disabled ? { disabled: disableLearnBuild } : {})}
      />
    ));
  }

  // Renderiza el bloque de actividad, incluyendo el texto que se visualiza
  renderActivity() {
    const { width, rootStore } = this.props;
    const block = BMC_BLOCKS.KEY_ACTIVITIES;
    const cards = rootStore.activityStore.activityEdit.getSelectedCardsByBlock(
      block
    );
    let fillColor = '#ddd';
    let textColor = '#555';
    // Actualiza colores en función de si existen tarjetas seleccionadas y la actividad está en foco
    if (cards[0]) {
      fillColor = '#6883A4';
      textColor = 'white';
    }
    if (block === rootStore.igBusinessStore.currentBlock) {
      fillColor = '#0fa69a';
      textColor = 'white';
    }
    return (
      <Group>
        {/* Componente de texto cuya altura se mide para ajustar el layout */}
        <Text
          ref="description"
          x={10}
          y={60}
          width={width}
          align="left"
          fontSize={16}
        />
      </Group>
    );
  }

  // Después de montar el componente, se obtiene la altura del texto para almacenarla en el estado
  componentDidMount() {
    this.setState({
      cardDescriptionHeight: this.refs.description.getHeight()
    });
  }

  // Actualiza la altura almacenada del texto si esta cambia al actualizar el componente
  componentDidUpdate(prevProps, prevState) {
    const nextHeight = this.refs.description.getHeight();
    if (this.state.cardDescriptionHeight !== nextHeight) {
      this.setState({ cardDescriptionHeight: nextHeight });
    }
  }

  // Renderiza el gráfico completo, combinando actividad, líneas, flechas y bloques
  render() {
    const positions = this.getPositions();
    return (
      <Group>
        {this.renderActivity()}
        {this.renderLines(positions, '#0fa69a')}
        {this.renderArrows(positions)}
        {this.renderBlocks(positions)}
      </Group>
    );
  }
}

export default ActivityArchitecture;
